import styled from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';

export const animationDuration = 200;

export const Wrapper = styled.div.attrs(() => ({
  role: 'dialog',
}))`
  position: relative;
  z-index: ${(props) => props.theme.zindex.panel};
`;

export const UiBlock = styled.div.attrs(() => ({
  'aria-hidden': 'true',
  role: 'button',
}))<{ $hideOnLargeScreen?: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(var(--c-overlay-rgb), 0.2);
  transition: opacity ${animationDuration}ms ease;

  .panel-appear & {
    opacity: 0;
  }

  .panel-appear-active & {
    opacity: 1;
  }

  .panel-exit & {
    opacity: 1;
  }

  .panel-exit-active & {
    opacity: 0;
  }

  /* Hide overlay on large screens for side-by-side layout */
  @media (min-width: 1920px) {
    ${(props) =>
      props.$hideOnLargeScreen &&
      `
      display: none;
    `}
  }
`;

export const PanelWrapper = styled.article<{ $wide?: boolean; $leftAdjust?: boolean }>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  background-color: var(--c-bg-main);
  box-shadow: 0 ${size(0.5)} ${size(1)} rgba(var(--c-overlay-rgb), 0.1);
  z-index: 1;
  transition: transform ${animationDuration}ms ease;

  ${(props) => props.theme.breakpoint.Sm} {
    max-width: ${(props) => (props.$wide ? '1200px' : '520px')};

    .panel-appear & {
      transform: translateX(100%);
    }

    .panel-appear-active & {
      transform: translateX(0);
    }

    .panel-exit & {
      transform: translateX(0);
    }

    .panel-exit-active & {
      transform: translateX(100%);
    }
  }

  /* Side-by-side layout for large screens */
  @media (min-width: 1920px) {
    ${(props) =>
      props.$leftAdjust &&
      `
      right: 520px;
    `}
  }

  ${(props) => props.theme.breakpoint.LtSm} {
    top: auto;
    max-height: 100%;

    .panel-appear & {
      transform: translateY(100%);
    }

    .panel-appear-active & {
      transform: translateY(0);
    }

    .panel-exit & {
      transform: translateY(0);
    }

    .panel-exit-active & {
      transform: translateY(100%);
    }
  }
`;

export const Header = styled.header`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  min-height: 45px;
  padding-top: ${size(4)};
  padding-left: ${size(4)};
  padding-right: ${size(4)};

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: ${size(3)};
    background-color: var(--c-bg-main);
    mask-image: ${(props) => props.theme.mask.gradient.toBottom};
    z-index: 10;
  }

  ${(props) => props.theme.breakpoint.Md} {
    padding-top: ${size(5)};
    padding-bottom: 0;

    &:after {
      height: ${size(5)};
    }
  }
`;

export const Heading = styled.div`
  flex: 1 1 auto;

  h1 {
    font-size: 1.5rem;
  }
`;

export const Close = styled.div`
  flex-shrink: 0;
  margin: ${size(-2)};
  padding-left: ${size(3)};
`;

export const CloseBtn = styled(ButtonReset)`
  position: relative;
  display: block;
  padding: ${size(2)};
  font-size: ${size(3)};
  color: var(--c-text-main);
  transform-origin: 50% 50%;
  transition: transform 200ms ease;
  z-index: 11;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${size(6)};
    height: ${size(6)};
    border-radius: 50%;
    background-color: var(--c-accent-bg);
    opacity: 0;
    transform: translate(-50%, -50%);
    transition: opacity 200ms ease;
    z-index: -1;
  }

  &:active {
    transform: scale(0.9);
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: ${size(4)};
`;

export const Footer = styled.footer`
  position: relative;
  flex-shrink: 0;
  padding: ${size(2)} ${size(4)};

  ${(props) => props.theme.breakpoint.Md} {
    padding: ${size(2)} ${size(4)};
  }
`;

export const FooterAction = styled.div.attrs(() => ({
  role: 'group',
}))`
  display: flex;
  align-items: center;
  margin: ${size(-0.5)};

  ${(props) => props.theme.breakpoint.Sm} {
    justify-content: flex-end;
  }
`;

export const FooterActionItem = styled.div`
  padding: ${size(0.5)};

  ${(props) => props.theme.breakpoint.LtSm} {
    flex: 1 1 50%;
  }
`;
